/**
 * Client Relationship Management — pure state-mutation actions.
 * Reads/writes through Zustand. Helpers in /data/clientLoyalty.js do the heavy lifting.
 */
import {
  buildInitialClientLoyalty,
  applyRelationshipEvent,
  RELATIONSHIP_EVENTS,
  RECURRENT_CLIENTS,
  CLIENT_BY_ID,
  getLoyaltyTier,
} from '../../data/clientLoyalty';
import { CLIENT_ACTIONS } from '../../data/clientActions';
import { generateId, buildSollicitedContract } from '../helpers';
import { computeServicePenalties, buildPenaltyMessage } from '../helpers/clientServicePenalties';

/**
 * Mapping (eventId, newTierId) → list of tier-bound templates to push
 * immediately when the player triggers a relationship event. Each tuple
 * picks ONE template at random from the array, so the same event can
 * surface different reactions across runs.
 *
 * The hook fires only when the score actually moved (we compare pre/post),
 * and only when the chosen template's `tierFilter` matches the NEW tier of
 * the client — keeps the narrative coherent.
 */
const EVENT_TIER_HOOKS = {
  // Annulation joueur : grosse claque → menace forte si Méfiant.
  contract_cancelled: {
    mefiant:    ['cli-mefiant-threat-01', 'cli-mefiant-complaint-01'],
    neutre:     ['cli-complaint-01'],
    confiant:   ['cli-complaint-01'],
    fidele:     ['cli-complaint-01'],
    partenaire: ['cli-complaint-01'],
  },
  // Livraison en retard répétée : plainte si chute en Méfiant.
  contract_completed_late: {
    mefiant:    ['cli-mefiant-complaint-01'],
    neutre:     ['cli-complaint-01'],
  },
  // Refus d'un contrat proposé : friction modérée.
  contract_refused: {
    mefiant:    ['cli-mefiant-threat-01'],
  },
  // Renégociation refusée : crispation.
  renegotiation_refused: {
    mefiant:    ['cli-mefiant-complaint-01'],
  },
  // Yearly bonus → on remonte le moral si Fidèle/Partenaire (rare event).
  yearly_no_incident: {
    fidele:     ['cli-fidele-praise-01'],
    partenaire: ['cli-partenaire-gift-01', 'cli-partenaire-recommendation-01'],
  },
  // Weekly delivery → on garde silencieux la plupart du temps. On déclenche
  // un message seulement quand un Partenaire/Fidèle traverse une transition
  // (géré via la randomisation du `tickMessages`). Pas de push automatique
  // ici pour éviter le spam — 1 livraison hebdo = 1 + de score, c'est tout.
};

/**
 * Push a tier-contextual narrative message right after an event. Picks a
 * random template among the eligible list and binds `{clientName}` to the
 * actual recurring client whose loyalty just changed.
 *
 * No-op when no mapping exists for the event/tier combination.
 */
function _dispatchEventMessage(get, clientId, eventId, newScore) {
  const mapping = EVENT_TIER_HOOKS[eventId];
  if (!mapping) return;
  const tier = getLoyaltyTier(newScore);
  if (!tier) return;
  const candidates = mapping[tier.id];
  if (!candidates || candidates.length === 0) return;
  const templateId = candidates[Math.floor(Math.random() * candidates.length)];
  const client = CLIENT_BY_ID[clientId];
  if (!client) return;
  // Defensive: pushMessageFromTemplate must exist (messageSlice loaded).
  const push = get().pushMessageFromTemplate;
  if (typeof push !== 'function') return;
  push(templateId, {
    clientName: client.name,
    senderName: client.name,
    senderId: client.id,
  });
}

export const createCrmSlice = (set, get) => ({
  // ============= STATE =============
  // { [clientId]: { score, events, coolingUntilDay, yearlyHadIncident, yearlyIncidentResetDay } }
  clientLoyalty: {},

  // ============= ACTIONS =============
  /** Called from createCompany() — seeds the initial roster. */
  bootstrapClientLoyalty: () => {
    set({ clientLoyalty: buildInitialClientLoyalty() });
  },

  /**
   * Generic action — applies one of the catalogued RELATIONSHIP_EVENTS.
   * Caller passes clientId, eventId and optional extra metadata for history.
   * Returns the new score so callers can React (e.g. trigger UI toast).
   */
  applyClientEvent: (clientId, eventId, extra = {}) => {
    const state = get();
    if (!clientId || !state.clientLoyalty[clientId]) return null;
    if (!RELATIONSHIP_EVENTS[eventId]) return null;
    const day = state.totalDays || 0;
    const newLoyalty = applyRelationshipEvent(state.clientLoyalty, clientId, eventId, day, extra);
    set({ clientLoyalty: newLoyalty });
    const newScore = newLoyalty[clientId]?.score ?? null;
    // Fire a contextual narrative message if a hook exists. Wrapped in
    // try/catch so a broken template never blocks the state mutation.
    try { _dispatchEventMessage(get, clientId, eventId, newScore); }
    catch (err) { console.warn('[crm] dispatch failed:', err); }
    // Pedagogical hook : the very first late delivery is the player's
    // first encounter with the client-payment risk axis — open the
    // contextual mini-tutorial. Idempotent (the slice de-dupes via
    // `contextualTutorialsSeen`). Other negative events (cancellation,
    // damage) don't trigger this — we want to teach the "late = signal,
    // not yet a disaster" framing on the most common case first.
    if (eventId === 'contract_completed_late') {
      try { get().triggerContextualTutorial('firstLatePayment'); }
      catch (err) { console.warn('[crm] contextual tuto fire failed:', err); }
    }
    return newScore;
  },

  /**
   * 18/09/2026 — Impact client renforcé (REX apprenant). Appelé après le
   * set() final de nextDay avec la map des contrats manqués du jour :
   * pénalité contractuelle débitée, réputation entamée, fidélité du client
   * dégradée et, tous les 6 manquements, bouche-à-oreille négatif vers les
   * autres clients du même segment. Cf. helpers/clientServicePenalties.js.
   */
  tickClientServicePenalties: (contractMisses = {}) => {
    const state = get();
    if (!contractMisses || Object.keys(contractMisses).length === 0) return [];
    const penalties = computeServicePenalties(state.activeContracts || [], contractMisses, state.clientLoyalty || {});
    if (penalties.length === 0) return [];
    const day = state.totalDays || 0;
    const totalPenalty = penalties.reduce((s, p) => s + p.penalty, 0);
    const totalRep = penalties.reduce((s, p) => s + p.repHit, 0);
    const notifications = [...(state.notifications || [])];
    penalties.forEach(p => {
      notifications.push({
        id: generateId(),
        type: 'client_service_penalty',
        title: `Pénalité client — ${p.clientName}`,
        message: `${p.contractName} : ${p.missedDays} livraisons manquées → −${p.penalty.toLocaleString('fr-FR')} € et réputation −${p.repHit}${p.wordOfMouth ? ` · ${p.affectedClientIds.length} autre(s) client(s) alerté(s)` : ''}.`,
        day,
        read: false,
      });
    });
    set({
      treasury: (state.treasury || 0) - totalPenalty,
      reputation: Math.max(0, Math.min(100, (state.reputation || 0) - totalRep)),
      notifications: notifications.slice(-40),
      clientPenaltiesTotal: (state.clientPenaltiesTotal || 0) + totalPenalty,
    });
    penalties.forEach(p => {
      if (p.clientId) {
        get().applyClientEvent(p.clientId, 'service_penalty', { contractId: p.contractId, value: p.penalty });
        p.affectedClientIds.forEach(id => get().applyClientEvent(id, 'word_of_mouth_negative', { note: `Fiabilité mise en cause par ${p.clientName}` }));
      }
      if (typeof get().pushCustomMessage === 'function') {
        try { get().pushCustomMessage(buildPenaltyMessage(p, day)); }
        catch (err) { console.warn('[crm] penalty message failed:', err); }
      }
    });
    return penalties;
  },

  /**
   * Yearly check (called once a game year by coreSlice during the annual close).
   * For each client without any incident in the past year, applies the
   * +10 "1 an sans incident" bonus. Resets the yearlyHadIncident flag.
   */
  runYearlyClientLoyaltyTick: () => {
    const state = get();
    const day = state.totalDays || 0;
    const next = { ...state.clientLoyalty };
    let bonusCount = 0;
    Object.keys(next).forEach(cid => {
      const data = next[cid];
      if (!data) return;
      if (!data.yearlyHadIncident) {
        const newScore = Math.max(0, Math.min(100, (data.score || 0) + RELATIONSHIP_EVENTS.yearly_no_incident.delta));
        next[cid] = {
          ...data,
          score: newScore,
          events: [
            { eventId: 'yearly_no_incident', label: RELATIONSHIP_EVENTS.yearly_no_incident.label, delta: RELATIONSHIP_EVENTS.yearly_no_incident.delta, day },
            ...(data.events || []),
          ].slice(0, 30),
          yearlyHadIncident: false,
          yearlyIncidentResetDay: day,
        };
        bonusCount += 1;
      } else {
        next[cid] = { ...data, yearlyHadIncident: false, yearlyIncidentResetDay: day };
      }
    });
    set({ clientLoyalty: next });
    return bonusCount;
  },

  /**
   * Monthly reputation drift — clients give a tiny +0.5/month boost when
   * the company has a strong public reputation (>70). Reflects organic trust.
   * Called by coreSlice at month close.
   */
  runMonthlyReputationDrift: () => {
    const state = get();
    if ((state.reputation || 0) < 70) return 0;
    const day = state.totalDays || 0;
    const delta = RELATIONSHIP_EVENTS.reputation_drift_positive.delta;
    const next = { ...state.clientLoyalty };
    Object.keys(next).forEach(cid => {
      const data = next[cid];
      next[cid] = {
        ...data,
        score: Math.max(0, Math.min(100, (data.score || 0) + delta)),
      };
    });
    set({ clientLoyalty: next });
    return Object.keys(next).length;
  },

  /** Reset slice (called by resetGame). */
  resetClientLoyalty: () => set({ clientLoyalty: {} }),

  /**
   * 28/07/2026 — Leviers d'action commerciale proactifs (retour user prod).
   * Visite commerciale, invitation événement, sollicitation de mission
   * (succès probabiliste → contrat ponctuel dédié), plan de reconquête
   * (relation dégradée : gros gain + levée du gel « Méfiant »).
   */
  performClientAction: (clientId, actionId) => {
    const state = get();
    const action = CLIENT_ACTIONS.find(a => a.id === actionId);
    const client = RECURRENT_CLIENTS.find(c => c.id === clientId);
    if (!action || !client) return { ok: false, reason: 'unknown' };
    const today = state.totalDays || 0;
    const key = `${clientId}:${actionId}`;
    const until = (state.clientActionCooldowns || {})[key] || 0;
    if (today < until) return { ok: false, reason: 'cooldown', daysLeft: until - today };
    const data = (state.clientLoyalty || {})[clientId] || { score: 30 };
    if (action.minScore != null && (data.score || 0) < action.minScore) {
      return { ok: false, reason: 'score_too_low', minScore: action.minScore };
    }
    if (action.maxScore != null && (data.score || 0) >= action.maxScore) {
      return { ok: false, reason: 'score_too_high', maxScore: action.maxScore };
    }
    if ((state.treasury || 0) < action.cost) return { ok: false, reason: 'treasury' };

    const patch = {
      clientActionCooldowns: { ...(state.clientActionCooldowns || {}), [key]: today + action.cooldownDays },
      treasury: (state.treasury || 0) - action.cost,
    };
    const result = { ok: true, actionId };

    if (actionId === 'visite-commerciale') {
      patch.clientLoyalty = applyRelationshipEvent(state.clientLoyalty, clientId, 'commercial_visit', today);
      result.message = `${client.name} apprécie votre visite : +${RELATIONSHIP_EVENTS.commercial_visit.delta} fidélité.`;
    } else if (actionId === 'invitation-evenement') {
      patch.clientLoyalty = applyRelationshipEvent(state.clientLoyalty, clientId, 'event_invitation', today);
      patch.notoriety = Math.min(100, (state.notoriety || 0) + 1);
      result.message = `${client.name} a répondu présent : +${RELATIONSHIP_EVENTS.event_invitation.delta} fidélité, +1 notoriété.`;
    } else if (actionId === 'solliciter-mission') {
      const chance = Math.min(0.75, 0.25 + ((data.score || 0) / 200));
      const success = Math.random() < chance;
      if (success) {
        const contract = buildSollicitedContract(state, client);
        if (contract) {
          patch.availableContracts = [...(state.availableContracts || []), contract];
          patch.clientLoyalty = applyRelationshipEvent(state.clientLoyalty, clientId, 'mission_request_success', today);
          result.success = true;
          result.contract = contract;
          result.message = `${client.name} vous confie « ${contract.name} » — visible sur le tableau des contrats (Tableau de bord) pendant 10 jours.`;
        } else {
          result.success = false;
          result.message = `${client.name} n'a pas de flux compatible avec votre flotte actuelle.`;
        }
      } else {
        result.success = false;
        result.message = `${client.name} n'a pas de besoin à confier pour le moment. Retentez dans ${action.cooldownDays} jours.`;
      }
    } else if (actionId === 'plan-reconquete') {
      let loyalty = applyRelationshipEvent(state.clientLoyalty, clientId, 'winback_plan', today);
      const cur = loyalty[clientId];
      if (cur && (cur.coolingUntilDay || 0) > today) {
        loyalty = { ...loyalty, [clientId]: { ...cur, coolingUntilDay: today } };
      }
      patch.clientLoyalty = loyalty;
      result.message = `Plan de reconquête engagé chez ${client.name} : +${RELATIONSHIP_EVENTS.winback_plan.delta} fidélité, gel levé le cas échéant.`;
    }

    patch.notifications = [
      ...(state.notifications || []),
      {
        id: generateId(),
        type: 'client_action',
        title: `Action client — ${client.name}`,
        message: result.message,
        day: today,
        read: false,
      },
    ];
    set(patch);
    return result;
  },

  /** Convenience selector — returns the [clientId, data] array sorted by score desc. */
  listClientsByLoyalty: () => {
    const state = get();
    return RECURRENT_CLIENTS.map(c => ({
      client: c,
      data: state.clientLoyalty[c.id] || { score: 30, events: [], coolingUntilDay: 0 },
    })).sort((a, b) => (b.data.score || 0) - (a.data.score || 0));
  },
});
